import React, { useState, useEffect, useRef } from 'react';
import {
  initializeGoogleAuth,
  signInWithGoogle,
  signOutFromGoogle,
  getUserProfile,
  isSignedIn,
  onAuthStateChanged,
  checkFedCMSupport
} from '../utils/googleAuth';

const GoogleLoginButton = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [fedcmSupported, setFedcmSupported] = useState(false);
  const [authMethod, setAuthMethod] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    // Check FedCM support
    const fedcmSupport = checkFedCMSupport();
    setFedcmSupported(fedcmSupport);
    console.log('FedCM supported:', fedcmSupport);

    // Initialize Google Auth when component mounts
    const initAuth = async () => {
      try {
        await initializeGoogleAuth();
        const signedIn = isSignedIn();
        setIsAuthenticated(signedIn);

        if (signedIn) {
          const profile = getUserProfile();
          setUserProfile(profile);
        }
      } catch (error) {
        console.error('Failed to initialize Google Auth:', error);
      } finally {
        setIsLoading(false);
      }
    };

    // Wait for Google API to load
    const checkGoogleAPI = () => {
      if (window.google) {
        initAuth();
      } else {
        setTimeout(checkGoogleAPI, 100);
      }
    };

    checkGoogleAPI();

    // Listen for auth state changes
    const handleAuthChange = (signedIn, userInfo) => {
      setIsAuthenticated(signedIn);
      setUserProfile(userInfo);
    };

    onAuthStateChanged(handleAuthChange);

    // Handle clicks outside dropdown
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup
    return () => {
      window.removeEventListener('googleSignIn', handleAuthChange);
      window.removeEventListener('googleSignOut', handleAuthChange);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSignIn = async () => {
    try {
      setIsLoading(true);
      setAuthMethod(fedcmSupported ? 'FedCM' : 'One Tap');

      await signInWithGoogle();
      // The signInWithGoogle will trigger the custom event
      // which will be handled by the onAuthStateChanged listener
    } catch (error) {
      console.error('Sign in failed:', error);
      setAuthMethod('');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      setIsLoading(true);
      await signOutFromGoogle();
      setUserProfile(null);
      setIsAuthenticated(false);
      setAuthMethod('');
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Sign out failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  if (isLoading) {
    return (
      <li className="page-scroll">
        <span className="btn btn-sm" style={{ color: 'white', fontSize: '12px' }}>
          {authMethod ? `Signing in via ${authMethod}...` : 'Loading...'}
        </span>
      </li>
    );
  }

  if (isAuthenticated && userProfile) {
    return (
      <li className="page-scroll">
        <div className="dropdown" style={{ display: 'inline-block', position: 'relative' }} ref={dropdownRef}>
          <button
            className="btn btn-sm google-user-dropdown dropdown-toggle"
            type="button"
            onClick={toggleDropdown}
            title={`Signed in via ${authMethod || 'Google'}`}
          >
            {userProfile.picture && (
              <img
                src={userProfile.picture}
                alt="Profile"
                className="google-profile-img"
                referrerPolicy="no-referrer"
              />
            )}
            {userProfile.given_name || userProfile.name || 'User'}
            {fedcmSupported && (
              <span style={{ fontSize: '10px', marginLeft: '3px', opacity: 0.7 }}>
                🔒
              </span>
            )}
          </button>
          {isDropdownOpen && (
            <ul className="dropdown-menu google-dropdown-menu" style={{
              display: 'block',
              position: 'absolute',
              top: '100%',
              right: '0',
              left: 'auto',
              zIndex: 1000,
              minWidth: '200px',
              backgroundColor: 'white',
              border: '1px solid #ccc',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              margin: '2px 0 0 0',
              padding: '5px 0'
            }}>
              <li>
                <span style={{ padding: '10px', display: 'flex', alignItems: 'center', fontSize: '12px', color: '#666' }}>
                  {userProfile.picture && (
                    <img
                      src={userProfile.picture}
                      alt={userProfile.name || 'User'}
                      referrerPolicy="no-referrer"
                      style={{ width: '32px', height: '32px', borderRadius: '50%', marginRight: '10px' }}
                    />
                  )}
                  {userProfile.email}
                </span>
              </li>
              {authMethod && (
                <li>
                  <span style={{ padding: '5px 10px', display: 'block', fontSize: '10px', color: '#999' }}>
                    Auth: {authMethod}
                  </span>
                </li>
              )}
              <li role="separator" className="divider" style={{
                height: '1px',
                margin: '5px 0',
                overflow: 'hidden',
                backgroundColor: '#e5e5e5'
              }}></li>
              <li>
                <button
                  className="google-dropdown-item"
                  onClick={handleSignOut}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    padding: '10px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    color: '#333'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  Sign Out
                </button>
              </li>
            </ul>
          )}
        </div>
      </li>
    );
  }

  return (
    <li className="page-scroll">
      <button
        className="btn btn-sm google-login-btn"
        onClick={handleSignIn}
        title={fedcmSupported ? 'Sign in with Google (FedCM enabled)' : 'Sign in with Google'}
      >
        Sign In
        {fedcmSupported && (
          <span style={{ fontSize: '10px', marginLeft: '3px', opacity: 0.7 }}>
            🔒
          </span>
        )}
      </button>
    </li>
  );
};

export default GoogleLoginButton;
